import { useRef } from "react";
import { motion, useInView } from "motion/react";
import { ArrowRight, Check } from "lucide-react";
import { useLanguage } from "../../i18n/LanguageContext";
import { translations } from "../../i18n/translations";

const silkEase = [0.22, 1, 0.36, 1] as const;

const sectionVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.09 } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: silkEase } },
};

export function Outcome() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(sectionRef, { once: true, amount: 0.15 });

  const { language } = useLanguage();
  const t = translations[language].outcome;

  return (
    <section className="pt-12 pb-24 px-6">
      <motion.div
        ref={sectionRef}
        className="max-w-3xl mx-auto"
        variants={sectionVariants}
        initial="hidden"
        animate={isInView ? "visible" : "hidden"}
      >
        <motion.h2
          className="text-3xl md:text-4xl mb-10 text-center"
          style={{ color: "var(--foreground)", fontFamily: "var(--font-display)" }}
          variants={itemVariants}
        >
          {t.heading}
        </motion.h2>

        <ul className="flex flex-col gap-3 mb-12">
          {t.items.map((item, i) => (
            <motion.li
              key={i}
              className="flex items-center gap-4 px-5 py-4 rounded-xl"
              style={{
                border: "1px solid var(--border)",
                background: "var(--surface-1)",
              }}
              variants={itemVariants}
            >
              <span
                className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
                style={{
                  background: "rgba(212, 149, 106, 0.10)",
                  color: "var(--gold)",
                }}
              >
                <Check className="w-4 h-4" strokeWidth={2.25} />
              </span>
              <span
                className="text-base md:text-lg leading-snug"
                style={{ color: "var(--foreground-muted)", fontFamily: "var(--font-body)" }}
              >
                {item}
              </span>
            </motion.li>
          ))}
        </ul>

        <motion.p
          className="text-xl md:text-2xl text-center leading-relaxed"
          style={{ color: "var(--foreground)", fontFamily: "var(--font-display)" }}
          variants={itemVariants}
        >
          {t.closing}{" "}
          <em style={{ color: "var(--gold)", fontStyle: "italic" }}>{t.closingAccent}</em>
        </motion.p>

        <motion.div className="flex justify-center mt-10" variants={itemVariants}>
          <motion.a
            href="#kontakt"
            className="inline-flex items-center gap-3 px-8 py-4 rounded-xl text-base"
            style={{
              background:
                "linear-gradient(135deg, var(--gold) 0%, var(--gold-bright) 50%, var(--gold) 100%)",
              backgroundSize: "200% 100%",
              color: "#0A0A0B",
              fontFamily: "var(--font-body)",
              fontWeight: 600,
              boxShadow: "0 0 40px var(--glow-gold-strong), 0 4px 24px rgba(0,0,0,0.4)",
              textDecoration: "none",
            }}
            whileHover={{
              scale: 1.02,
              boxShadow: "0 0 60px var(--glow-gold-strong), 0 8px 32px rgba(0,0,0,0.5)",
            }}
            whileTap={{ scale: 0.98 }}
          >
            {t.cta}
            <ArrowRight className="w-4 h-4" strokeWidth={2} />
          </motion.a>
        </motion.div>
      </motion.div>
    </section>
  );
}
